import React, { useEffect, useState } from 'react';
import { Container, Heading, Text, Button, Flex, Box } from '@radix-ui/themes';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircledIcon, EnvelopeClosedIcon } from '@radix-ui/react-icons';
import confetti from 'canvas-confetti';
import './PaymentSuccess.css';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [countdown, setCountdown] = useState(15);

  // Extract any query parameters like session_id that might be needed
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get('session_id');

  useEffect(() => {
    // Trigger confetti animation when component mounts
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;

    const randomInRange = (min, max) => {
      return Math.random() * (max - min) + min;
    };

    const confettiInterval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      
      if (timeLeft <= 0) {
        clearInterval(confettiInterval);
        return;
      }
      
      confetti({
        particleCount: 3,
        angle: randomInRange(55, 125),
        spread: randomInRange(50, 70),
        origin: { x: randomInRange(0.1, 0.9), y: randomInRange(0.1, 0.5) },
        colors: ['#26a69a', '#4CAF50', '#2196F3', '#3f51b5', '#9c27b0'],
        startVelocity: randomInRange(25, 45),
        gravity: 1.2,
        drift: randomInRange(-1, 1),
      });
    }, 200);

    // Auto-redirect to dashboard after 15 seconds
    const redirectTimer = setTimeout(() => {
      navigate('/dashboard');
    }, 15000);

    // Countdown timer
    const countdownInterval = setInterval(() => {
      setCountdown(prevCount => {
        if (prevCount <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);

    // Clean up the timers when component unmounts
    return () => {
      clearInterval(confettiInterval);
      clearTimeout(redirectTimer);
      clearInterval(countdownInterval);
    };
  }, [navigate]);

  return (
    <Container size="3" className="payment-success-container">
      <Flex 
        direction="column" 
        gap="6" 
        align="center" 
        justify="center" 
        style={{ 
          minHeight: '80vh',
          padding: '2rem',
          maxWidth: '800px',
          margin: '0 auto'
        }}
      >
        <Box
          className="success-card"
          style={{
            background: 'linear-gradient(145deg, #f0fff4, #e6fffa)',
            padding: '3rem',
            borderRadius: '16px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
            width: '100%',
            textAlign: 'center'
          }}
        >
          <CheckCircledIcon 
            className="success-icon-pulse"
            color="green" 
            width={80} 
            height={80} 
            style={{ marginBottom: '1rem' }} 
          />
          
          <Heading 
            className="success-title"
            size="8" 
            style={{ 
              color: '#2e7d32',
              marginBottom: '1.5rem'
            }}
          >
            Payment Successful!
          </Heading>
          
          <Text 
            className="success-message"
            size="5" 
            style={{ 
              color: '#3e3e3e',
              lineHeight: 1.6,
              marginBottom: '2rem'
            }}
          >
            Thank you for your payment. Your transaction has been completed successfully.
          </Text>

          <Box 
            className="email-notification"
            style={{ 
              marginTop: '1rem',
              padding: '1rem',
              background: 'rgba(66, 133, 244, 0.08)',
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}
          >
            <Flex align="center" justify="center" gap="2">
              <EnvelopeClosedIcon color="#1a73e8" width={24} height={24} />
              <Text size="3" style={{ color: '#1a73e8', fontWeight: 500 }}>
                Please check your email for your temporary password and school code.
              </Text>
            </Flex>
          </Box>

          <Box style={{ marginTop: '1rem' }}>
            <Text size="2" className="countdown-text" style={{ color: '#666', marginBottom: '1rem' }}>
              You'll be redirected to the dashboard automatically in <span style={{ fontWeight: 'bold' }}>{countdown}</span> seconds.
            </Text>
            
            <Button 
              className="success-return-button"
              size="3" 
              color="green" 
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '0 2rem',
                height: '48px',
                fontWeight: 600,
                transition: 'all 0.2s ease'
              }}
            >
              Return to Dashboard
            </Button>
          </Box>
        </Box>
      </Flex>
    </Container>
  );
};

export default PaymentSuccess; 